// ---------------------------------------------------------------------------
// track.ts — the two kinds of track, as one type the player can hold
// ---------------------------------------------------------------------------
//
// STOCK  a file on our server, uploaded by a platform admin, streamed over HTTP
//        with Range support. Every hotel can play every stock track.
// LOCAL  a file on this one machine, reached through a FileSystemFileHandle kept
//        in this browser's IndexedDB. Never uploaded. It therefore exists only
//        on the device that added it, and it can go missing when someone moves
//        or deletes the file — which is why `status` is part of the type rather
//        than something a caller may forget to look at.
//
// A discriminated union rather than one struct with nullable fields: the
// compiler then refuses code that reads `storageKey` off a local track, instead
// of that becoming `undefined` at runtime inside an <audio> src.
// ---------------------------------------------------------------------------

/** Whether a device-local file is currently reachable. */
export type LocalTrackStatus =
  /** Verified present and readable. */
  | 'ok'
  /** The handle is intact but the browser wants a fresh user gesture. */
  | 'needs-permission'
  /** The file has been moved, renamed or deleted. */
  | 'missing'
  /** Not checked yet this session. */
  | 'unknown';

export interface StockTrack {
  source: 'STOCK';
  id: string;
  title: string;
  artist: string | null;
  album: string | null;
  genre: string | null;
  durationSec: number;
  mimeType: string;
}

export interface LocalTrack {
  source: 'LOCAL';
  /** Always `local:<uuid>`. Doubles as the playlist `localRef`. */
  id: string;
  title: string;
  artist: string | null;
  /** Null until the browser has read metadata off the file at least once. */
  durationSec: number | null;
  sizeBytes: number;
  status: LocalTrackStatus;
}

export type Track = StockTrack | LocalTrack;

export const LOCAL_ID_PREFIX = 'local:';

export function isStockTrack(t: Track): t is StockTrack {
  return t.source === 'STOCK';
}

export function isLocalTrack(t: Track): t is LocalTrack {
  return t.source === 'LOCAL';
}

/** Is this id a device-local reference rather than a stock uuid? */
export function isLocalRef(id: string): boolean {
  return id.startsWith(LOCAL_ID_PREFIX);
}

/**
 * Where the browser fetches a stock track.
 *
 * Built from the id, never from a stored path or key. The server looks the key
 * up itself, so nothing a client sends can influence a filesystem path.
 */
export function stockStreamUrl(trackId: string): string {
  return `/api/v1/music/tracks/${encodeURIComponent(trackId)}/stream`;
}

/**
 * Can this track be played right now?
 *
 * Stock tracks always can. A local track can only when it has been verified
 * present — and `'unknown'` counts as playable because verification happens at
 * play time anyway; refusing it here would make a freshly-loaded library look
 * entirely broken until something checked every row.
 */
export function isPlayable(t: Track): boolean {
  if (isStockTrack(t)) return true;
  return t.status === 'ok' || t.status === 'unknown';
}

// ---------------------------------------------------------------------------
// Playlist payloads
// ---------------------------------------------------------------------------

/**
 * One playlist entry as the API accepts it.
 *
 * Exactly one variant, mirroring the database's
 * `CHECK ((track_id IS NOT NULL) <> (local_ref IS NOT NULL))`. Expressing it as
 * a union means a caller cannot build a both-or-neither item to send in the
 * first place — the server and the schema are then the second and third lines of
 * defence rather than the only ones.
 */
export type PlaylistItemInput =
  | { trackId: string }
  | { localRef: string; localLabel: string };

/**
 * Turn tracks into playlist entries, in order.
 *
 * A local track is saved by reference. That reference resolves only on the
 * device holding the file, so the label travels with it — it is the only thing
 * another device can display for that row.
 *
 * A `'missing'` local track is still saved deliberately: the file may come back,
 * and dropping the entry would lose its position in the playlist for good.
 */
export function toPlaylistItemInputs(tracks: readonly Track[]): PlaylistItemInput[] {
  return tracks.map((t) =>
    isStockTrack(t)
      ? { trackId: t.id }
      : { localRef: t.id, localLabel: t.title },
  );
}

/**
 * Split a queue into what will play here and what will not.
 *
 * The save dialog needs both halves: it saves everything, and says plainly which
 * entries other devices will not be able to play. Silently dropping them would
 * be worse — the playlist would differ from what the user just built.
 */
export function partitionByAvailability(tracks: readonly Track[]): {
  playable: Track[];
  unavailable: LocalTrack[];
} {
  const playable: Track[] = [];
  const unavailable: LocalTrack[] = [];
  for (const t of tracks) {
    if (isLocalTrack(t) && !isPlayable(t)) unavailable.push(t);
    else playable.push(t);
  }
  return { playable, unavailable };
}

/** How many entries of this playlist will only ever play on one machine. */
export function countDeviceOnly(tracks: readonly Track[]): number {
  return tracks.reduce((n, t) => (isLocalTrack(t) ? n + 1 : n), 0);
}

// ---------------------------------------------------------------------------
// Display
// ---------------------------------------------------------------------------

/**
 * Seconds → 'm:ss', or 'h:mm:ss' past an hour.
 *
 * Null renders as '--:--' rather than '0:00': a local track whose duration has
 * not been read yet is unknown, and showing zero would look like a broken file.
 */
export function formatDuration(totalSeconds: number | null | undefined): string {
  if (totalSeconds === null || totalSeconds === undefined || !Number.isFinite(totalSeconds)) {
    return '--:--';
  }
  const s = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  const ss = String(seconds).padStart(2, '0');
  if (hours > 0) return `${hours}:${String(minutes).padStart(2, '0')}:${ss}`;
  return `${minutes}:${ss}`;
}

/** The subtitle under a track name. Falls back to the album, then nothing. */
export function trackSubtitle(t: Track): string {
  if (t.artist) return t.artist;
  if (isStockTrack(t) && t.album) return t.album;
  return '';
}
